"use server";

import bcrypt from "bcryptjs";
import { AuthError } from "next-auth";
import { prisma } from "@/lib/prisma";
import { signIn } from "@/auth";

export type FormState = { error?: string } | undefined;

function parseCredentials(formData: FormData) {
  const email = formData.get("email");
  const password = formData.get("password");

  if (typeof email !== "string" || !email.includes("@")) {
    return { error: "Enter a valid email address." } as const;
  }
  if (typeof password !== "string" || password.length < 8) {
    return { error: "Password must be at least 8 characters." } as const;
  }

  return { email, password } as const;
}

export async function signup(
  _prevState: FormState,
  formData: FormData
): Promise<FormState> {
  const parsed = parseCredentials(formData);
  if ("error" in parsed) return { error: parsed.error };
  const { email, password } = parsed;

  const existing = await prisma.user.findUnique({ where: { email } });
  if (existing) {
    return { error: "An account with that email already exists." };
  }

  const hashedPassword = await bcrypt.hash(password, 10);
  await prisma.user.create({ data: { email, hashedPassword } });

  try {
    await signIn("credentials", { email, password, redirectTo: "/dashboard" });
  } catch (error) {
    if (error instanceof AuthError) {
      return { error: "Account created, but sign-in failed. Try logging in." };
    }
    throw error;
  }
}

export async function login(
  _prevState: FormState,
  formData: FormData
): Promise<FormState> {
  const parsed = parseCredentials(formData);
  if ("error" in parsed) return { error: parsed.error };
  const { email, password } = parsed;

  try {
    await signIn("credentials", { email, password, redirectTo: "/dashboard" });
  } catch (error) {
    if (error instanceof AuthError) {
      return { error: "Invalid email or password." };
    }
    throw error;
  }
}
